/**
 * @module
 */

import {COMPILE, UPGRADE} from '@acng/frontend-stargazer';
import {guard, OBJECT, RECORD, STRING} from '@acng/frontend-rubicon';
import {IS_CHANGEABLE, IS_INPUT, IS_SELECT, IS_TEXTAREA} from '@acng/frontend-rubicon';
import {popup} from '@acng/frontend-discovery';
import {parseInteger} from '@acng/frontend-bounty/std/number.js';
import {ChangeEventController} from 'nova';
/** @import {UserProfile} from './profile.js' */
import {cdn} from '../../core/service/cdn.js';
import {inject} from '../../core/service/ng.js';
import {t} from '../../locale/config/translate.js';
import {cmToIn, inToCm, kgToLbs, lbsToKg} from '../../locale/service/units.js';
import {user} from '../user.js';
import {getOptions, VALID_FILLABLE} from '../model/user-profile.js';

class ProfileChange extends ChangeEventController {
  /**
   * @readonly
   */
  #profile;

  /**
   * @param {UserProfile} profile
   */
  constructor(profile) {
    super();
    this.#profile = profile;
  }

  /**
   * @override @type {import('nova').ChangeAction}
   */
  async change(input) {
    let {name: key, value} = input;

    switch (key) {
      case 'birthday':
        if (!value) {
          break;
        }

        const age = this.#ageFromBirthday(value);

        if (!age) {
          await popup(input).warn(await t('userPool.birthdayRegexFail'));

          return;
        }

        if (age.age < 18) {
          await popup(input).warn(await t('userPool.birthdayTooYoung'));
          input.value = input.getAttribute('value') ?? '';

          return;
        }

        value = inject('dateFilter')(age.birthday, 'yyyy-MM-dd');
        break;
      case 'height':
      case 'penislength':
      case 'penisperimeter':
        if (!user.usesMetricUnits()) {
          value = inToCm(parseInteger(value)).toString();
        }

        break;
      case 'weight':
        if (!user.usesMetricUnits()) {
          value = lbsToKg(parseInteger(value)).toString();
        }

        break;
    }

    await this.#profile.put({[key]: value});

    switch (key) {
      case 'gender':
        this.#profile.male = value == 'm';
        this.#profile.female = value == 'f';
    }
  }

  /**
   * @param {Element} element
   */
  async [COMPILE](element) {
    await this.#profile.get();

    ASSERT: guard(element, IS_CHANGEABLE);
    const {name} = element;

    let lengthUnit = 'inch';
    let weightUnit = 'lbs';

    if (user.usesMetricUnits()) {
      lengthUnit = 'cm';
      weightUnit = 'kg';
    }

    switch (name) {
      case 'description':
        ASSERT: guard(element, IS_TEXTAREA);
        element.autocomplete = 'off';
        element.maxLength = 4096;
        break;
      case 'birthday':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'off';
        element.placeholder = await t('userPool.birthdayFormat');
        element.type = 'text';
        break;
      case 'zip':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'postal-code';
        element.type = 'text';
        break;
      case 'city':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'address-level2';
        element.type = 'text';
        break;
      case 'country': {
        ASSERT: guard(element, IS_SELECT);
        element.autocomplete = name;
        const initialOption = new Option(await t('core.pleaseChoose'), '', true, true);
        initialOption.disabled = true;
        element.options.add(initialOption);

        const data = await cdn(`/language/${user.lang}/countries`);
        ASSERT: guard(data, OBJECT({countries: OBJECT({iso: RECORD(STRING, STRING)})}));

        for (const option of Object.entries(data.countries.iso)
          .sort((a, b) => new Intl.Collator(user.lang).compare(a[1], b[1]))
          .map(([value, text]) => new Option(text, value))) {
          element.options.add(option);
        }

        break;
      }
      case 'height':
      case 'penislength':
      case 'penisperimeter': {
        ASSERT: guard(element, IS_INPUT);
        element.type = 'number';
        element.placeholder = await t('core.inPortalLengthUnit', {lengthUnit});

        break;
      }
      case 'weight': {
        ASSERT: guard(element, IS_INPUT);
        element.type = 'number';
        element.placeholder = await t('core.inPortalWeightUnit', {weightUnit});

        break;
      }
      case 'gender':
      case 'ethnic':
      case 'eyecolor':
      case 'haircolor':
      case 'hairlength':
      case 'body':
      case 'bodyhair':
      case 'pubichair':
      case 'brasize':
      case 'glasses':
      case 'tattoos':
      case 'piercings':
      case 'marital':
      case 'smoking': {
        ASSERT: guard(element, IS_SELECT);
        element.autocomplete = 'off';
        const initialOption = new Option(await t('core.pleaseChoose'), '', true, true);
        initialOption.disabled = true;
        element.options.add(initialOption);

        for (const {value, text} of await getOptions(user.lang)(name)) {
          element.options.add(new Option(text, value));
        }

        break;
      }
    }
  }

  /**
   * @param {Element} element
   */
  [UPGRADE](element) {
    ASSERT: {
      guard(element, IS_CHANGEABLE);
      const {localName, name} = element;
      guard(element.name, VALID_FILLABLE, `<${localName} name="${name}">`);
    }

    const {name} = element;
    let value = `${this.#profile.data[name] ?? ''}`;

    if (!user.usesMetricUnits()) {
      switch (name) {
        case 'height':
        case 'penislength':
        case 'penisperimeter':
          value = `${cmToIn(parseInt(value))}`;
          break;
        case 'weight':
          value = `${kgToLbs(parseInt(value))}`;
          break;
      }
    }

    if (name == 'birthday') {
      value = inject('dateFilter')(value, 'dd.MM.yyyy');
    }

    element.value = value;
  }

  /**
   * @param {string} val
   */
  #ageFromBirthday(val) {
    var matches = /^(0?[1-9]|[12][0-9]|3[01])\.(0?[1-9]|1[012])\.((19|20)\d\d)$/.exec(val);

    if (!matches) {
      return false;
    }

    var birthday = new Date(parseInt(matches[3]), parseInt(matches[2]) - 1, parseInt(matches[1]));
    var today = new Date();
    var age = today.getFullYear() - birthday.getFullYear();
    if (
      today.getMonth() < birthday.getMonth() ||
      (today.getMonth() == birthday.getMonth() && today.getDate() < birthday.getDate())
    ) {
      age--;
    }
    return {age: age, birthday: birthday};
  }
}

export {ProfileChange};
